/**
 * OGraf graphics in the sequence editor (0.93.0): which template a clip uses, its schedule and the moment to show at
 * a playhead (frame-locked at the sequence's rate), newer versions, a new clip's defaults, and a single-graphic
 * preview.
 */
import { useEffect, useMemo, useRef } from 'react';
import {
  TICKS_PER_SECOND, msToFrameTicks, ografCarryData, ografCarryPartial, ografClipFrames, ografDefaultLengthMs, ografDefaults, ografIsSting, ografResolution,
  ografSchedule, ografTimestamp, renderAtData, ticksToFrameIndex, ticksToFrames,
  type GraphicData, type GraphicInstance, type GraphicTemplateDTO, type OgrafManifest, type Rational,
} from '@prism/contracts';
import { OgrafFrame, type OgrafFrameHandle } from './OgrafFrame';
import { useOgrafPackage } from './ografPackage';

export const isOgrafTemplate = (t: GraphicTemplateDTO | undefined): t is GraphicTemplateDTO & { ograf: OgrafManifest } => t?.engine === 'ograf-v1' && Boolean(t.ograf);

export const templateFor = (templates: GraphicTemplateDTO[], g: Pick<GraphicInstance, 'templateId' | 'templateVersion'>) =>
  templates.find((t) => t.id === g.templateId && t.version === g.templateVersion);

/** The newest ready version of the clip's graphics asset, when it is newer than the one the clip uses. */
export function newerVersion(templates: GraphicTemplateDTO[], g: Pick<GraphicInstance, 'templateId' | 'templateVersion'>): GraphicTemplateDTO | undefined {
  if (!g.templateId.startsWith('asset-')) return undefined;
  const newer = templates.filter((t) => t.id === g.templateId && t.version > g.templateVersion && t.ready !== false && t.enabled !== false);
  return newer.sort((a, b) => b.version - a.version)[0];
}

/** The data and updates a clip keeps when it moves to another version: values of fields that still exist. */
export function carryToVersion(next: GraphicTemplateDTO, g: GraphicInstance): { data: GraphicData; updates?: GraphicInstance['updates'] } {
  if (isOgrafTemplate(next)) {
    const updates = (g.updates ?? []).map((u) => ({ ...u, data: ografCarryPartial(next.ograf, u.data) })).filter((u) => Object.keys(u.data).length);
    return { data: ografCarryData(next.ograf, g.data), updates };
  }
  const old = renderAtData(g.data);
  return { data: Object.fromEntries(next.manifest.fields.map((f) => [f.id, f.id in old ? old[f.id]! : f.default])) };
}

/** A new clip of a template: its default length (OGraf: in + hold + out, a sting's own length) and its default data. */
export function newClipFor(t: GraphicTemplateDTO, holdSeconds: number, rate: Rational): { durationTicks: number; data: GraphicData } {
  if (isOgrafTemplate(t)) return { durationTicks: msToFrameTicks(ografDefaultLengthMs(t.ograf, holdSeconds), rate), data: ografDefaults(t.ograf) };
  return { durationTicks: Math.round(t.manifest.defaultDurationSeconds * TICKS_PER_SECOND), data: Object.fromEntries(t.manifest.fields.map((f) => [f.id, f.default])) };
}

/** A sting's fixed length on the timeline (null when the length is free). */
export const fixedLengthTicks = (t: GraphicTemplateDTO | undefined, rate: Rational): number | null =>
  isOgrafTemplate(t) && ografIsSting(t.ograf) ? msToFrameTicks(ografDefaultLengthMs(t.ograf, 4), rate) : null;

export const clipSchedule = (m: OgrafManifest, g: GraphicInstance, rate: Rational) =>
  ografSchedule(m, { data: g.data, stepMarks: g.stepMarks, updates: g.updates, durationTicks: g.durationTicks }, rate);

/**
 * What a clip shows at a playhead: the picture's frame (floor at the sequence rate) minus the clip's record-in
 * frame, as goToTime's timestamp; hidden outside the clip.
 */
export function clipMoment(g: Pick<GraphicInstance, 'recordInTicks' | 'durationTicks'>, rate: Rational, playhead: number): { ms: number; visible: boolean; frame: number } {
  const D = ografClipFrames(g.durationTicks, rate);
  const local = ticksToFrames(playhead, rate) - ticksToFrameIndex(g.recordInTicks, rate);
  const visible = local >= 0 && local < D;
  const frame = Math.max(0, Math.min(D - 1, local));
  return { ms: ografTimestamp(frame, rate), visible, frame };
}

/** One OGraf graphic in its own frame at a moment (the inspector, the bin, the asset page). */
export function OgrafPreview({ template, data, schedule, rate, ms, className, style }: {
  template: GraphicTemplateDTO & { ograf: OgrafManifest };
  data: GraphicData;
  schedule: ReturnType<typeof ografSchedule>;
  rate: Rational;
  ms: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const pkg = useOgrafPackage(template.id, template.version);
  const size = ografResolution(template.ograf);
  const frame = useRef<OgrafFrameHandle>(null);
  const mounts = useMemo(() => (pkg.data ? [{ instance: 'preview', pkg: pkg.data, manifest: template.ograf, data, schedule, frameRate: rate, z: 0 }] : []), [pkg.data, template.ograf, data, schedule, rate]);
  useEffect(() => { frame.current?.seek([{ instance: 'preview', ms, visible: true }]); }, [ms, mounts]);
  if (pkg.isError) return <div className="graphic-error">{(pkg.error as Error).message}</div>;
  return <OgrafFrame ref={frame} stage={size} mounts={mounts} title={`${template.ograf.name} preview`} className={className} style={style} />;
}
